/**
 * רישום אימות דו-שלבי — enrolment. `packages/authn`, 04 §2.2 / §2.3.
 *
 * ── WHAT ARRIVES, AND WHAT DOES NOT ─────────────────────────────────────────────────────────────
 *
 * A secret to enrol, an account label, ten recovery codes shown ONCE, and the policy that made this
 * required. What never arrives, here or anywhere: a password. 04 §2.2 — *"No path may ever produce,
 * transmit or display a password."*
 *
 * ── THE QR IS THE SERVER'S, AND ITS ABSENCE IS WRITTEN OUT ──────────────────────────────────────
 *
 * `provisioningUri` takes `issuer` and `accountName` as ARGUMENTS, deliberately: a hardcoded issuer
 * would be a product name baked into every enrolled device, not merely into the source, and a rename
 * cannot reach an authenticator app that was set up last year. So the URI — and the QR rendered from
 * it — come from the server with the issuer resolved from configuration.
 *
 * Until that arrives this screen shows the SECRET, which is the manual path every authenticator
 * supports, and states plainly that the QR is not available. It does not draw a placeholder pattern
 * that looks like a QR: a code nobody can scan is worse than a stated absence, and this milestone's
 * honesty rule is exactly about the difference.
 *
 * ── ENROLMENT IS UNAVOIDABLE WHERE IT IS REQUIRED ───────────────────────────────────────────────
 *
 * `afterFirstFactor` returns `MFA_ENROLMENT_REQUIRED` for a required-but-unenrolled principal, and
 * that principal is admitted to enrolment AND TO NOTHING ELSE. They are not let in "until they get
 * round to it" and are not silently downgraded to a password-only session — the two failure modes
 * that turn a mandate into a suggestion. For a platform account the requirement is the PRODUCT
 * FLOOR and no tenant policy can lower it, which is why the source of the requirement is named on
 * screen rather than just its existence.
 *
 * ── THE RECOVERY CODES ARE SHOWN ONCE ───────────────────────────────────────────────────────────
 *
 * Only their hashes are stored, so there is no later screen that can show them again, and saying so
 * at the moment they are displayed is the only time it helps.
 *
 * ── AND ENROLMENT IS ITSELF STEP-UP GUARDED ────────────────────────────────────────────────────
 *
 * `auth.mfa.enrol` can answer 403 with `need`, and that answer is NOT a denial: the gate allowed the
 * request, and the handler refused because the session is stale or a factor exists and has not been
 * presented. *Establishing a new second factor with a stale session is how an unattended browser
 * becomes an enrolled device.*
 *
 * So this screen distinguishes the three needs rather than showing one "אין הרשאה". `REAUTH` sends
 * the person to sign in; `MFA` sends them to the CHALLENGE, because they already hold a working
 * authenticator and enrolling again would create the second device the guard exists against; and
 * `ENROLMENT` is the one that ends here. `<ActionFailureNotice>` does that routing from `stepUpText`,
 * so this screen never decides which door it is.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { RECOVERY_CODE_COUNT, TOTP_DIGITS } from '../api/contract';
import { mfaPolicyHe } from '../api/adminVocabulary';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { readProvisioning } from '../api/provisioning';
import type { ProvisioningDetails } from '../api/provisioning';
import { fixtureMfaEnrolment, fixturesEnabled } from '../api/fixtures';
import { routes } from '../routes';

const TITLE_HE = 'רישום אימות דו-שלבי';

const REQUIRED_BY_HE: Readonly<Record<'product_floor' | 'tenant_policy' | 'not_required', string>> = {
  // No tenant setting can lower this one, and the wording says so rather than implying a choice.
  product_floor: 'אימות דו-שלבי הוא דרישת יסוד של המערכת עבור חשבון מסוג זה, ואינו ניתן לביטול.',
  tenant_policy: 'אימות דו-שלבי נדרש לפי מדיניות המשרד.',
  not_required: 'אימות דו-שלבי אינו חובה עבורך, ומומלץ בכל זאת.',
};

/** Base32 in groups of four, so it can be read aloud and typed without losing the place. */
export function groupSecret(secret: string): string {
  return (secret.match(/.{1,4}/gu) ?? []).join(' ');
}

/** Two writes, and the second one only exists because the first produced a challenge reference. */
type Outcome =
  | { readonly kind: 'awaitingConfirmation' }
  | { readonly kind: 'enrolled' }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

/**
 * CM-024. **THE SECRET COMES FROM THE SERVER.**
 *
 * This screen used to display `fixtureMfaEnrolment.secretBase32` — a fixed, published Base32 string
 * — as the manual key, to every user, while confirming against the server's real challenge. Two
 * defects in one line: a code from that secret could never match, so enrolment could not complete;
 * and anyone who did scan it enrolled a secret this repository publishes.
 *
 * The server already returned the provisioning URI. What was missing was the declaration of it and a
 * screen that read it. Nothing on the server changed.
 *
 * ── AND THE ORDER CHANGED WITH IT ───────────────────────────────────────────────────────────────
 *
 * The old screen showed recovery codes as step 2, BEFORE confirmation. They do not exist then:
 * `confirmMfaEnrolment` mints them and stores only their hashes, and it does that only once a code
 * has proved the secret reached the device. So the codes appear after confirmation, which is also
 * the only order in which the sentence *"these are shown once"* is true.
 */
export function MfaEnrolmentScreen(): JSX.Element {
  const [code, setCode] = useState('');
  const [challengeRef, setChallengeRef] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /** From the server's provisioning URI. Never persisted, never logged, never put in an address. */
  const [enrolment, setEnrolment] = useState<ProvisioningDetails | null>(null);
  /** Minted by the CONFIRMATION, so `null` until the code proved the secret arrived. */
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);

  const offer = fixturesEnabled() ? fixtureMfaEnrolment : null;

  /**
   * Begins enrolment. The response carries the provisioning URI — which CONTAINS THE SHARED SECRET —
   * and this screen never logs it, never stores it and never puts it in an address; it is displayed
   * once, as the manual key, in the block below.
   */
  async function begin(): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      const started = await callOperation('auth.mfa.enrol', { body: {} });
      const details = readProvisioning(started.provisioning);
      if (details === null) {
        // A provisioning URI this build cannot read is not a key to show anyway. Displaying the raw
        // string would hand somebody a value to type into an authenticator that will not accept it.
        // `unreachable` is the honest classification: the server answered, and what it answered
        // cannot be used. There is no arm for "we got something we could not read", and inventing
        // one would put a sixth answer in a taxonomy that exists to have five.
        setOutcome({ kind: 'failed', failure: { kind: 'unreachable' } });
        return;
      }
      setChallengeRef(started.challengeRef);
      setEnrolment(details);
      setOutcome({ kind: 'awaitingConfirmation' });
    } catch (err: unknown) {
      // A 403 here is usually a STEP-UP, not a denial. `<ActionFailureNotice>` names which challenge.
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    setSending(true);
    setOutcome(null);
    void (async () => {
      try {
        // The verification itself is `verifyTotp` on the server, with the replay defence that makes a
        // code single-use within its window. Nothing here decides whether a code is correct.
        //
        // Confirming with a code PROVES the secret reached the device. Enrolling without it would
        // leave a principal holding a factor they cannot present — a lockout the product created.
        // The challenge is the one `begin()` received. There is no arm for "confirm without having
        // begun": the route reads the ref from the body and would answer for whatever row it names.
        if (challengeRef === null) return;
        const confirmed = await callOperation('auth.mfa.enrolment.confirm', {
          body: { challengeRef, code },
        });
        // Shown ONCE. The response is the only time these exist outside the user's hands — the
        // server stores hashes — so they are held in component state and nowhere else.
        setRecoveryCodes(confirmed.recoveryCodes.split('\n').filter((line) => line !== ''));
        setOutcome({ kind: 'enrolled' });
      } catch (err: unknown) {
        setOutcome({ kind: 'failed', failure: classifyActionError(err) });
      } finally {
        setSending(false);
      }
    })();
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      provenanceHe="הסוד וקודי השחזור מופקים בשרת. המערכת שומרת את קודי השחזור בצורה חד-כיוונית בלבד ואינה יכולה להציג אותם שוב."
    >
      {offer === null ? null : (
        // The POLICY — which office requires MFA, and why — is still development data, and is
        // labelled as such by the shell's fixture ribbon. It carries no credential.
        <section className="notice" data-tone="info">
          <h2 className="notice__title">{mfaPolicyHe(offer.policy)}</h2>
          <p className="notice__body">{REQUIRED_BY_HE[offer.requiredBy]}</p>
        </section>
      )}

      <ol className="steps steps--stacked">
        <li className="steps__item" data-state="current">
          <span className="steps__index" dir="ltr">
            1
          </span>
          <span className="steps__label">הוספת החשבון ליישומון האימות</span>
        </li>
        <li className="steps__item" data-state="pending">
          <span className="steps__index" dir="ltr">
            2
          </span>
          <span className="steps__label">אישור בעזרת קוד מהיישומון</span>
        </li>
        <li className="steps__item" data-state="pending">
          <span className="steps__index" dir="ltr">
            3
          </span>
          <span className="steps__label">שמירת קודי השחזור — לאחר האישור</span>
        </li>
      </ol>

      <div className="actions-row">
        <button type="button" className="btn" onClick={() => void begin()} disabled={sending}>
          {sending ? 'שולח…' : 'התחלת רישום מול השרת'}
        </button>
        <a className="btn" href={routes.personalSecurity}>
          האבטחה שלי
        </a>
      </div>

      <section className="enrolment">
        <h2 className="enrolment__title">1 · הוספת החשבון ליישומון</h2>

        {enrolment === null ? (
          // Before `begin()` there is no secret, and there must be nothing here that looks like one.
          // This is where the fixture key used to sit.
          <div className="enrolment__qr enrolment__qr--absent" role="note">
            <p className="enrolment__qr-title">הרישום עדיין לא התחיל</p>
            <p className="enrolment__qr-body">
              המפתח נוצר בשרת ברגע שמתחילים את הרישום, ומוצג פעם אחת בלבד. עד אז אין כאן מפתח להוסיף
              ליישומון.
            </p>
          </div>
        ) : offer?.qrImageDataUri == null ? (
          // Stated, never drawn. A QR that cannot be scanned is worse than a written absence.
          <div className="enrolment__qr enrolment__qr--absent" role="note">
            <p className="enrolment__qr-title">קוד ה-QR אינו זמין בבנייה הזו</p>
            <p className="enrolment__qr-body">
              הקוד נוצר בשרת מתוך שם המערכת שמוגדר בהגדרות הפריסה. עד שהשרת יחובר אפשר להוסיף את
              החשבון ליישומון בעזרת המפתח הידני שלמטה — כל יישומון אימות תומך בכך.
            </p>
          </div>
        ) : (
          <img className="enrolment__qr" src={offer.qrImageDataUri} alt="קוד לסריקה ביישומון האימות" />
        )}

        <dl className="facts">
          <div className="facts__row">
            <dt className="facts__label">שם החשבון ביישומון</dt>
            <dd className="facts__value" dir="ltr">
              {enrolment?.accountLabel ?? '—'}
            </dd>
          </div>
          <div className="facts__row">
            <dt className="facts__label">מפתח ידני</dt>
            <dd className="facts__value">
              {enrolment === null ? (
                <span className="facts__absent">—</span>
              ) : (
                <code className="secret" dir="ltr">
                  {groupSecret(enrolment.secretBase32)}
                </code>
              )}
            </dd>
          </div>
          <div className="facts__row">
            <dt className="facts__label">סוג הקוד</dt>
            <dd className="facts__value">
              {`קוד מתחלף בן ${TOTP_DIGITS} ספרות, מתחדש כל 30 שניות`}
            </dd>
          </div>
        </dl>
      </section>

      <section className="enrolment">
        <h2 className="enrolment__title">2 · אישור</h2>
        <form className="form" onSubmit={submit}>
          <label className="field">
            <span className="field__label">הקוד המוצג ביישומון</span>
            <input
              className="field__input"
              dir="ltr"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={TOTP_DIGITS}
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              required
            />
          </label>
          <div className="actions-row">
            <button type="submit" className="btn btn--primary" disabled={sending}>
              {sending ? 'שולח…' : 'סיום הרישום'}
            </button>
          </div>
        </form>
      </section>

      {outcome === null ? null : outcome.kind === 'awaitingConfirmation' ? (
        <StateBlock
          titleHe="הרישום החל וטרם הושלם"
          bodyHe="השרת הנפיק סוד לרישום. הרישום ייכנס לתוקף רק לאחר הזנת קוד מהיישומון — כך אנחנו יודעים שהסוד באמת הגיע למכשיר."
          tone="unavailable"
          nextStep={<p>יש להזין את הקוד המוצג ביישומון בשלב 3.</p>}
        />
      ) : outcome.kind === 'enrolled' ? (
        <StateBlock
          titleHe="הרישום הושלם"
          bodyHe="מעכשיו תידרש להזין קוד מהיישומון בהתחברות. קודי השחזור שהוצגו אינם ניתנים להצגה חוזרת."
          nextStep={
            <a className="btn btn--primary" href={routes.personalSecurity}>
              חזרה למסך האבטחה שלי
            </a>
          }
        />
      ) : (
        <ActionFailureNotice failure={outcome.failure} />
      )}
    </AppShell>
  );
}
